import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type FieldBlurPoint = { x: number; y: number; blur: number };

export interface FieldBlurParams {
  points: FieldBlurPoint[];
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const FieldBlur: Filter<FieldBlurParams> = {
  id: "field-blur",
  displayName: "Field Blur",
  defaultParams: { points: [{ x: 0.5, y: 0.5, blur: 10 }] },

  apply(input: PixelBuffer, params: FieldBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) { dst.set(src); return out; }

    const points = params.points.length > 0 ? params.points : [{ x: 0.5, y: 0.5, blur: 0 }];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const nx = x / w, ny = y / h;

        let totalBlur = 0, totalWeight = 0;
        for (const p of points) {
          const dx = nx - p.x, dy = ny - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const weight = Math.exp(-dist * 5);
          totalBlur += p.blur * weight;
          totalWeight += weight;
        }

        const blur = totalWeight > 0 ? totalBlur / totalWeight : 0;

        if (blur < 0.5) {
          const o = (y * w + x) * 4;
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
          continue;
        }

        const r = Math.round(blur);
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

        for (let dy2 = -r; dy2 <= r; dy2++) {
          for (let dx2 = -r; dx2 <= r; dx2++) {
            const sx = Math.max(0, Math.min(w - 1, x + dx2));
            const sy = Math.max(0, Math.min(h - 1, y + dy2));
            const si = (sy * w + sx) * 4;
            sumR += src[si]!; sumG += src[si + 1]!; sumB += src[si + 2]!; sumA += src[si + 3]!;
            count++;
          }
        }

        const o = (y * w + x) * 4;
        if (count > 0) {
          dst[o] = clamp8(Math.round(sumR / count));
          dst[o + 1] = clamp8(Math.round(sumG / count));
          dst[o + 2] = clamp8(Math.round(sumB / count));
          dst[o + 3] = clamp8(Math.round(sumA / count));
        } else {
          dst[o] = src[o]!; dst[o + 1] = src[o + 1]!; dst[o + 2] = src[o + 2]!; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
